package com.trevorwiebe.timesheet.core.presentation.common

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TimePicker
import androidx.compose.material3.TimePickerDefaults
import androidx.compose.material3.TimePickerState
import androidx.compose.material3.rememberTimePickerState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.domain.model.Punch
import com.trevorwiebe.timesheet.punch.presentation.uiUtils.UiPunch
import kotlinx.datetime.LocalDateTime
import org.jetbrains.compose.resources.painterResource
import timesheet.composeapp.generated.resources.Res
import timesheet.composeapp.generated.resources.error_circle

@OptIn(ExperimentalMaterial3Api::class)
@Composable
actual fun NativeTimePicker(
    punch: Punch?,
    currentPunches: List<UiPunch>?,
    onDismiss: () -> Unit,
    onTimeSelected: (Punch) -> Unit,
) {
    val showDialog = punch != null

    if (showDialog) {
        var error by remember { mutableStateOf("") }

        fun updatePunch(timeState: TimePickerState, punch: Punch): Punch {
            val hour = timeState.hour
            val minute = timeState.minute
            val currentDateTime = punch.dateTime
            val newDateTime = LocalDateTime(
                year = currentDateTime.year,
                monthNumber = currentDateTime.monthNumber,
                dayOfMonth = currentDateTime.dayOfMonth,
                hour = hour,
                minute = minute,
                second = currentDateTime.second,
                nanosecond = currentDateTime.nanosecond
            )
            return punch.copy(dateTime = newDateTime)
        }

        val timeState = rememberTimePickerState(
            initialHour = punch.dateTime.hour,
            initialMinute = punch.dateTime.minute,
            is24Hour = false
        )

        Dialog(onDismissRequest = { onDismiss() }) {
            Card(shape = RoundedCornerShape(16.dp)) {
                Column(modifier = Modifier.padding(16.dp)) {
                    TimePicker(
                        state = timeState,
                        colors = TimePickerDefaults.colors(
                            clockDialColor = MaterialTheme.colorScheme.surface,
                            clockDialSelectedContentColor = MaterialTheme.colorScheme.onTertiary,
                            selectorColor = MaterialTheme.colorScheme.tertiary,
                            containerColor = MaterialTheme.colorScheme.onTertiary,
                            periodSelectorSelectedContainerColor = MaterialTheme.colorScheme.tertiary,
                            periodSelectorSelectedContentColor = MaterialTheme.colorScheme.onTertiary,
                            periodSelectorUnselectedContentColor = MaterialTheme.colorScheme.onSurface,
                            timeSelectorSelectedContentColor = MaterialTheme.colorScheme.onTertiary,
                            timeSelectorSelectedContainerColor = MaterialTheme.colorScheme.tertiary,
                        )
                    )

                    AnimatedVisibility(error.isNotEmpty()) {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.Center,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Icon(
                                painter = painterResource(Res.drawable.error_circle),
                                contentDescription = null,
                                tint = MaterialTheme.colorScheme.error,
                                modifier = Modifier.padding(end = 8.dp)
                            )
                            Text(
                                text = error,
                                color = MaterialTheme.colorScheme.error,
                                modifier = Modifier.fillMaxWidth()
                            )
                        }
                    }

                    DialogButtonRow(
                        confirmButtonText = "OK",
                        onConfirmClick = {
                            val updatedPunch = updatePunch(timeState, punch)
                            val conflictError = currentPunches
                                ?.filterNot { it.punchIn == punch || it.punchOut == punch }
                                ?.firstOrNull { uiPunch ->
                                    val newTime = updatedPunch.dateTime
                                    val uiPunchOut = uiPunch.punchOut
                                    if (uiPunchOut == null) {
                                        newTime >= uiPunch.punchIn.dateTime
                                    } else {
                                        val start = uiPunch.punchIn.dateTime
                                        val end = uiPunchOut.dateTime
                                        newTime > start && newTime < end
                                    }
                                }?.let { conflict ->
                                    val conflictPunchOut = conflict.punchOut
                                    if (conflictPunchOut == null) {
                                        val punchInTime = conflict.punchIn.dateTime
                                        "This time must be before the current punch in at ${Util.toFriendlyTime(punchInTime)}"
                                    } else {
                                        val start = conflict.punchIn.dateTime
                                        val end = conflictPunchOut.dateTime
                                        "This time conflicts with current punch in at ${Util.toFriendlyTime(start)} and the punch out at ${Util.toFriendlyTime(end)}"
                                    }
                                }
                            if (conflictError != null) {
                                error = conflictError
                            } else {
                                error = ""
                                onTimeSelected(updatedPunch)
                                onDismiss()
                            }
                        },
                        dismissButtonText = "Cancel",
                        onDismissClick = onDismiss,
                    )
                }
            }
        }
    }
}
